import { Col, Row, Button, Spinner, Container, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useUser } from "../../../../utils/auth";
import EditClaimFollowUpSendEmail from "./EditClaimFollowUpSendEmail";
import { ClaimFollowUpCommentListDto, ClaimFollowUpItemDto, ClaimOutletContext, ClaimFileDto } from "../../types";
import AlertMessage from "../../../../components/AlertMessage";
import { formatTimestampToDDMMYYYYHHMM } from "../../../../utils/formatters/DateFormatter";
import { IoArrowRedoOutline } from "react-icons/io5";
import EditClaimFollowUpReplyEmail from "./EditClaimFollowUpReplyEmail";
import commentIcon from "@/assets/message-dots-thin-full.svg";
import requestIcon from "@/assets/envelope-thin-full.svg";
import filesClipIcon from "@/assets/paperclip-vertical-thin-full.svg";
import { useClaimFollowUpByIdClaim } from "../../hooks/useClaimFollowUpByIdClaim";
import { useClaimFollowUpComment } from "../../hooks/useClaimFollowUpComment";
import EditClaimFollowUpCommentsFiles from "./EditClaimFollowUpCommentsFiles";
import { useModal } from "../../../../hooks/useModal";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { useAlert } from "../../../../hooks/useAlert";
import { deleteComment } from "../../api/ClaimFollowUpCommentAPI";
import ClaimFollowUpCommentForm from "./ClaimFollowUpCommentForm";
import { ClaimFileOriginTypeEnum } from "../../types/enums";
import { ClaimStatusEnum } from "../../types/ClaimStatusEnum";

export default function EditClaimFollowUp() {
    const { t } = useTranslation('claim');
    const { userInfo } = useUser();

    const { claimUpdate, setClaimUpdate, sendEmail, setSendEmail, reloadClaim } = useOutletContext<ClaimOutletContext>();
    const { data: claimFollowUps, isLoading, refetch: refetchFollowUps } = useClaimFollowUpByIdClaim(claimUpdate.id ?? 0);
    const { data: claimFollowUpsComments, refetch: refetchFollowUpsComments } = useClaimFollowUpComment(claimUpdate.id ?? 0);
    const [replyEmail, setReplyEmail] = useState<boolean>(false);
    const [addComment, setAddCommment] = useState<boolean>(false);
    const [commentFiles, setCommentFiles] = useState<boolean>(false);
    const [fileOriginType, setFileOriginType] = useState<ClaimFileOriginTypeEnum | null>(null);
    const [commentData, setCommentData] = useState<ClaimFollowUpCommentListDto | ClaimFollowUpItemDto>();
    const [editingCommentId, setEditingCommentId] = useState<number | null>(null);

    const [professionalId, setSetProfessionalId] = useState<number>(0);
    const [professionalName, setSetProfessionalName] = useState<string>('');
    const [readOnly, setReadOnly] = useState<boolean>();

    const [searchParams] = useSearchParams();
    const mode = searchParams.get('mode');
    
    useEffect(() => {
        if (!claimUpdate) return;
        setReadOnly(claimUpdate.status === ClaimStatusEnum.Closed || mode == 'view');
    }, [claimUpdate]);

    useEffect(() => {
        if (!claimUpdate.id) return;

        refetchFollowUps();
        refetchFollowUpsComments();
    }, [claimUpdate.id, refetchFollowUps, refetchFollowUpsComments]);

    const refreshFollowUpValues = async () => {
        await Promise.all([refetchFollowUps(), refetchFollowUpsComments()]);
        reloadClaim();
    };

    const canSendEmail =
        sendEmail &&
        claimUpdate != null &&
        claimUpdate.id != null &&
        claimUpdate.id !== 0;

    const handleReplyEmail = (professionalId: number, professionalName: string) => {
        setReplyEmail(true)
        setSetProfessionalId(professionalId);
        setSetProfessionalName(professionalName)
    }

    const timelineItems: Array<
        | { kind: "request"; date: number; data: ClaimFollowUpItemDto }
        | { kind: "response"; date: number; data: ClaimFollowUpItemDto }
        | { kind: "comment"; date: number; data: ClaimFollowUpCommentListDto }
    > = [
        ...(claimFollowUps ?? []).flatMap((cf) => {
            const items: Array<{ kind: "request" | "response"; date: number; data: ClaimFollowUpItemDto }> = [];
            if (cf.createdDate) {
                items.push({ kind: "request", date: cf.createdDate, data: cf });
            }
            if (cf.response && cf.response !== "" && cf.responseDate) {
                items.push({ kind: "response", date: cf.responseDate, data: cf });
            }
            return items;
        }),
        ...(claimFollowUpsComments ?? []).map((cf) => ({
            kind: "comment" as const,
            date: cf.createdDate,
            data: cf,
        })),
    ].sort((a, b) => b.date - a.date);

    const { showModal, modalData, openModal, closeModal, errorModal, currentItem} = useModal<ClaimFollowUpCommentListDto>();
    const { showAlert, hideAlert, alert, isVisible } = useAlert({ fromLocation: false });
    
    const confirmSubmitModal = async () => {

        try {
            await deleteComment(currentItem?.id ?? 0);
            closeModal()
            await refreshFollowUpValues();
            showAlert({
                message: t("claim:followUpComment.commentDeletedSuccesfully"),
                type: "success",
                duration: 8000
            });
        } catch (error) {
            closeModal()

            const fetchError = error as ApiErrorResponse;
            if(fetchError?.message == "COMMENT_OUT_OF_TIME"){
                showAlert({
                    message: t("claim:followUpComment.deleteCommentOutOfTimeError"),
                    type: "danger",
                    duration: 8000
                });
                return;
            }
            else if(fetchError?.message == "COMMENT_NOT_SAME_CLIENT"){
                showAlert({
                    message: t("claim:followUpComment.deleteCommentNotSameUserError"),
                    type: "danger",
                    duration: 8000
                });
                return;
            }
            else{
                showAlert({
                    message: fetchError?.message ?? t("common:requestError.errorBadRequest"),
                    type: "danger",
                    duration: 8000
                });
            }
        }
    };

    const canDeleteComment = (comment: ClaimFollowUpCommentListDto) => {
        const hoursToEdit = Number(comment.claimHoursToDeleteComment);
        if (!Number.isFinite(hoursToEdit) || hoursToEdit <= 0) return false;

        const commentDate = typeof comment.createdDate === "string"
            ? Number(comment.createdDate)
            : comment.createdDate;

        if (!commentDate) return false;

        const normalizedCreatedDate = commentDate < 1e12 ? commentDate * 1000 : commentDate;
        const editDeadline = normalizedCreatedDate + hoursToEdit * 60 * 60 * 1000;
        const isWithinTimeWindow = Date.now() <= editDeadline;

        return isWithinTimeWindow;
    };


    const COMMENT_MODAL_HEADERS = {
        titleDelete: t("claim:modal.modalDeleteCommentTitle"),
        subtitleDelete: t("claim:modal.modalDeleteCommentSubtitle"),
    };

    const canEditComment = (comment: ClaimFollowUpCommentListDto) => {
        const hoursToEdit = Number(comment.claimHoursToEditComment);
        if (!Number.isFinite(hoursToEdit) || hoursToEdit <= 0) return false;

        const commentDate = typeof comment.createdDate === "string"
            ? Number(comment.createdDate)
            : comment.createdDate;

        if (!commentDate) return false;

        const normalizedCreatedDate = commentDate < 1e12 ? commentDate * 1000 : commentDate;
        const editDeadline = normalizedCreatedDate + hoursToEdit * 60 * 60 * 1000;
        const isWithinTimeWindow = Date.now() <= editDeadline;
        const isSameUser = parseInt(userInfo.id) == comment.createdUser;

        return isWithinTimeWindow && isSameUser;
    };

    const onEditComment = (comment: ClaimFollowUpCommentListDto) => {        
        setEditingCommentId(comment.id ?? null);
        setAddCommment(true);
    };

    const onDeleteComment = async (comment: ClaimFollowUpCommentListDto) => {
         openModal(
            comment,
            COMMENT_MODAL_HEADERS.titleDelete,
            COMMENT_MODAL_HEADERS.subtitleDelete,
            "deleteWithoutInput",
            ""
        );
    };

    const hasFollowUpFiles = (files: ClaimFileDto[], originType?: ClaimFileOriginTypeEnum) => {
        return files.length > 0 && (originType != undefined ? files.some(file => file.originType === originType) : true);
    }

    return (
        <> 
            <ModalGlobal
                show={showModal}
                onClose={closeModal}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                error={errorModal?.endDate || errorModal?.errorModal}
                onSave={confirmSubmitModal}
                isLoading={isLoading}
            />
            {isLoading && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}

            {canSendEmail ? (
                <EditClaimFollowUpSendEmail
                    setSendEmail={setSendEmail}
                    claim={claimUpdate}
                    comment={claimUpdate.comment ?? ""}
                    professionals={claimUpdate.professionals ?? []}
                    claimUpdate={claimUpdate}
                    setClaimUpdate={setClaimUpdate}
                    onSaved={refreshFollowUpValues}
                />
            ) : replyEmail ? (
                <EditClaimFollowUpReplyEmail
                    claim={claimUpdate}
                    professionalId={professionalId}
                    professionalName={professionalName}
                    setReplyEmail={setReplyEmail}
                    setMessageSuccess={(message: string) => {
                        showAlert({
                            message: message,
                            type: "success",
                            duration: 5000
                        })}}
                    onSaved={refreshFollowUpValues}
                />
            ) : addComment ? (
                <ClaimFollowUpCommentForm
                    claim={claimUpdate}
                    commentId={editingCommentId}
                    onCancel={() => {
                        setAddCommment(false);
                        setEditingCommentId(null);
                    }}
                    onSaved={() => {
                        setAddCommment(false);
                        setEditingCommentId(null);
                        showAlert({
                            message: t("claim:followUpComment.addCommentSavedSuccesfully"),
                            type: "success",
                            duration: 5000
                        });
                        refreshFollowUpValues();
                    }}
                    professionalId={professionalId}
                    professionalName={professionalName}
                    setReplyEmail={setReplyEmail}
                />
            ): commentFiles ? (
                <EditClaimFollowUpCommentsFiles
                    claimFollowUpsComment={commentData}
                    setClaimFollowUpsComment={setCommentData}
                    onCancel={() => { setCommentFiles(false); refetchFollowUpsComments(); }}
                    fileOriginType={fileOriginType}
                />
            ) : (
                <Row className="w-100">
                    {isVisible && (
                      <AlertMessage
                          message={alert!.message}
                          type={alert!.type}
                          onClose={hideAlert}
                      />
                    )}

                    <Row>
                        <h5>{t("claim:detail.monitoringTask")}</h5>
                    </Row>

                    <Row className="justify-content-end">
                        <Col xs="auto">
                            <Button variant="secondary" disabled={readOnly} onClick={() => setAddCommment(true)}>
                                {t("claim:button.analyzeAddComment")}
                            </Button>
                        </Col>
                      
                        <Col xs="auto">
                            <Button 
                                variant="secondary" disabled={readOnly} 
                                onClick={() => setSendEmail(true)}
                            >
                                {t("claim:button.sendEmail")}
                            </Button>
                        </Col>
                    </Row>

                    <Row className="mt-3">
                        <Container>
                            {timelineItems.map((item, index) => {
                                if (item.kind === "request") {
                                    const cf = item.data;
                                    return (
                                        <Card key={`request-${index}`} className="mb-2">
                                            <Card.Header className="d-flex align-items-center">
                                                <Row>
                                                    <strong>
                                                        {formatTimestampToDDMMYYYYHHMM(cf.createdDate)} -  {cf.createdUserFullName}
                                                    </strong>
                                                    <strong>
                                                        {cf.typeDescription}
                                                    </strong>
                                                </Row>
                                            </Card.Header>
                                            <Card.Body>
                                                <Row>
                                                    <Col xs={1} md={1} className="mt-2"></Col>
                                                    <Col xs={10} md={10} className="ps-0">
                                                        <Row className="align-items-start">
                                                            <Col xs={12} md={12} style={{ whiteSpace: 'pre-line' }}>{cf.request}</Col>
                                                        </Row>
                                                    </Col>
                                                    {
                                                        hasFollowUpFiles(cf.files ?? [], ClaimFileOriginTypeEnum.Request) &&
                                                            <Col xs={1} md={1} className="d-flex justify-content-end align-items-start">
                                                                <Button variant="link" className="p-0 border-0"                                                                
                                                                    onClick={() => {
                                                                            setFileOriginType(ClaimFileOriginTypeEnum.Request);
                                                                            setCommentFiles(true);
                                                                            setCommentData(cf);
                                                                        }}>
                                                                    <img style={{ marginRight: "5px", filter: "invert(18%) sepia(99%) saturate(1200%) hue-rotate(185deg) brightness(85%)" }} src={filesClipIcon} alt="files" width={20} height={20} />
                                                                </Button>
                                                            </Col>
                                                    }
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    );
                                }
                                if (item.kind === "response") {
                                    const cf = item.data;
                                    return (
                                        <Card key={`response-${index}`} className="mb-2">
                                            <Card.Header className="d-flex align-items-center">
                                                <img
                                                    style={{
                                                        marginRight: "5px",
                                                        filter: "invert(18%) sepia(99%) saturate(1200%) hue-rotate(185deg) brightness(85%)"
                                                    }}
                                                    title={t("common:buttons.buttonSurveys")}
                                                    src={requestIcon}
                                                    alt={t("common:buttons.buttonSurveys")}
                                                    width={25}
                                                    height={25}
                                                />
                                                <Row>
                                                    <strong>
                                                        {formatTimestampToDDMMYYYYHHMM(cf.responseDate)} - 
                                                        {cf.professionalName} ({cf.professionalTypeDescription ? cf.professionalTypeDescription + "-" : ""} {cf.professionalServiceDescription})
                                                    </strong>
                                                </Row>
                                            </Card.Header>
                                            <Card.Body>
                                                <Row>
                                                    <Col xs={1} md={1} className="mt-2">
                                                        <img
                                                            src="/principalFets.png"
                                                            alt="User"
                                                            style={{ width: '30px' }}
                                                            title={t("claim:followUpReply.toggleIcon")}
                                                        />
                                                    </Col>

                                                    <Col xs={11} md={11} className="ps-0">                                                        
                                                        <Row>
                                                            <strong>
                                                                {cf.typeDescription}
                                                            </strong>
                                                        </Row>

                                                        <Row className="align-items-start">
                                                            <Col xs={10} md={10} style={{ whiteSpace: 'pre-line' }}>{cf.response}</Col>
                                                            <Col xs={2} md={2} className="ps-0 d-flex justify-content-end">
                                                                {
                                                                    hasFollowUpFiles(cf.files ?? [], ClaimFileOriginTypeEnum.Response) &&
                                                                        <Button variant="link" className="p-0 border-0"                                                                    
                                                                            onClick={() => {
                                                                                setFileOriginType(ClaimFileOriginTypeEnum.Response);
                                                                                setCommentFiles(true);
                                                                                setCommentData(cf);
                                                                            }}>
                                                                            <img style={{ marginRight: "15px", filter: "invert(18%) sepia(99%) saturate(1200%) hue-rotate(185deg) brightness(85%)" }} src={filesClipIcon} alt="files" width={20} height={20} />
                                                                        </Button>
                                                                }
                                                                <Button
                                                                    className="btn-gris-circle"
                                                                    disabled={readOnly} 
                                                                    onClick={() => handleReplyEmail(cf.professionalId, cf.professionalName)}>
                                                                    <IoArrowRedoOutline />
                                                                </Button>
                                                                
                                                            </Col>
                                                        </Row>
                                                    </Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    );
                                }
                                
                                const cf = item.data;
                                return (
                                    <Card key={`comment-${index}`} className="mb-2">
                                        <Card.Header className="d-flex align-items-center">
                                            <img
                                                style={{
                                                    marginRight: "5px",
                                                    filter: "invert(18%) sepia(99%) saturate(1200%) hue-rotate(185deg) brightness(85%)"
                                                }}
                                                title={t("common:buttons.buttonSurveys")}
                                                src={commentIcon}
                                                alt={t("common:buttons.buttonSurveys")}
                                                width={25}
                                                height={25}
                                            />
                                            <Row>
                                                <strong>
                                                    {formatTimestampToDDMMYYYYHHMM(cf.createdDate)} -
                                                    {cf.createdUserFullName}
                                                </strong>
                                            </Row>
                                            <div className="ms-auto d-flex gap-2">
                                                {canEditComment(cf) && (
                                                    <Button
                                                        onClick={() => onEditComment(cf)}
                                                        className="btn btn-sm btn-gris2 btn-table-action">
                                                        <i className="bi bi-pencil" title={t("common:buttons.buttonEdit")}></i>
                                                    </Button>
                                                )}
                                                
                                                {canDeleteComment(cf) && (
                                                <Button
                                                    onClick={() => onDeleteComment(cf)}
                                                    className="btn btn-sm btn-gris2 btn-table-action">
                                                    <i className="bi bi-x" title={t("common:buttons.buttonDelete")}></i>
                                                </Button>
                                                )}
                                            </div>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                <Col md={{span: 11, offset: 1}} className="ps-0">                                                    
                                                    <Row>
                                                        <strong>
                                                            {cf.followUpDemandTypeDescription?.name}
                                                        </strong>
                                                    </Row>
                                                    <Row className="align-items-start" >
                                                        <Col xs={10} md={10} style={{ whiteSpace: 'pre-line' }}>{cf.comment}</Col>
                                                        {
                                                            hasFollowUpFiles(cf.files ?? []) &&
                                                                <Col xs={2} md={2} className="ps-0 d-flex justify-content-end">
                                                                    <span
                                                                        title={
                                                                            (cf.files?.length ?? 0) > 0
                                                                                ? t("claim:followUpCommentFiles.viewAttachments")
                                                                                : t("claim:followUpCommentFiles.noAttachments")
                                                                        }>
                                                                        <Button
                                                                            variant="link"
                                                                            className="p-0 border-0"
                                                                            disabled={(cf.files?.length ?? 0) === 0}
                                                                            onClick={() => {
                                                                                setFileOriginType(null);
                                                                                setCommentFiles(true);
                                                                                setCommentData(cf);
                                                                            }}>
                                                                            <img
                                                                                style={{ marginRight: "5px", filter: "invert(18%) sepia(99%) saturate(1200%) hue-rotate(185deg) brightness(85%)" }}
                                                                                src={filesClipIcon}
                                                                                alt="files"
                                                                                width={20}
                                                                                height={20} />
                                                                        </Button>
                                                                    </span>
                                                                </Col>
                                                        }
                                                    </Row>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                );
                            })}
                        </Container>
                    </Row>
                </Row>
            )}
        </>
    );
}




